import * as z from 'zod/v4';

/**
 * Get the keys of an object as a typed array.
 *
 * @param object Object to define typed keys for
 * @returns typed object array
 */
export function typedObjectKeys<TObject extends object>(object: TObject): (keyof TObject)[] {
  return Object.keys(object) as (keyof TObject)[];
}

/**
 * Get the values of an object as a typed array.
 *
 * @param obj Object to define typed values for
 * @returns typed object array
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function typedObjectValues<TRecord extends Record<string, any>>(obj: TRecord): [(typeof obj)[keyof TRecord]] {
  return Object.values(obj) as [(typeof obj)[keyof TRecord]];
}

/**
 * Inverts an Object and returns its corresponding values as keys with types.
 *
 * @param object Object to invert
 * @returns Inverted object
 */
export function invertObject<TKey extends string, TVal extends number | string>(
  object: Record<TKey, TVal>
): Readonly<Record<TVal, TKey>> {
  const invertedObject: Record<TVal, TKey> = {} as Record<TVal, TKey>;
  for (const key in object) {
    if (Object.hasOwn(object, key)) {
      const value = object[key];
      invertedObject[value] = key;
    }
  }

  return invertedObject;
}

/**
 * Creates a Zod enum from an object's keys.
 *
 * @param obj Object to create enum from
 * @returns Zod schema enum
 */
export function zodEnumFromObjectKeys<TKey extends string>(
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  obj: Record<TKey, any>
): z.ZodEnum<{ [K in keyof { [J in TKey]: J }]: { [J in TKey]: J }[K] }> {
  const keys = Object.keys(obj) as TKey[];

  return z.enum(keys);
}

type ZodSchemaOutputType<TSchema extends z.ZodType> = z.infer<TSchema>;
type ZodSchemaTransformFn<TValue, TRet> = (value: TValue) => TRet;

/**
 * Create a transformed schema from a Zod schema.
 *
 * @param schema Zod schema to transform
 * @param transformFn Transformation function to run on zoo schema
 * @returns Transformed Zod schema
 */
export function createTransformedSchema<TSchema extends z.ZodType, TFn>(
  schema: TSchema,
  transformFn: ZodSchemaTransformFn<ZodSchemaOutputType<TSchema>, TFn>
): z.ZodType<TFn> {
  return schema.transform(transformFn);
}
